import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Calendar, Share2, ArrowLeft, RefreshCw } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, RevealGroup } from "@/components/Reveal";
import ArticleCard from "@/components/ArticleCard";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import { DuckBadge } from "@/components/DuckMascot";
import { InlineCTA, StickyCTA } from "@/components/GuideCTA";
import { useSolidHeader } from "@/components/HeaderThemeContext";
import { getGuide, GUIDES } from "@/data/guides";

// Where the inline lead capture goes.
//
// Target depth, not heading count. An earlier version placed it before the
// third heading, which lands around 30% into a short guide but 35–40% into a
// long one — on an eighteen-screen piece that's six screens down, which is
// most of the way back to the problem we were fixing.
//
// So: the first heading at or past ~22% of the body, never the opening one.
// The reader has banked a section of free advice, the break is natural, and
// the depth stays consistent whether the guide runs 1,200 words or 1,800.
function ctaIndex(body = []) {
  const h2s = body.reduce((acc, b, i) => (b.type === "h2" ? [...acc, i] : acc), []);
  const target = body.length * 0.22;
  const pick = h2s.slice(1).find((i) => i >= target);
  if (pick !== undefined) return pick;
  if (h2s.length >= 2) return h2s[h2s.length - 1];
  return Math.max(1, Math.floor(body.length * 0.35));
}

// Inline formatting inside body copy.
//
// Guide text is plain strings, not HTML, so this parses the two marks the
// writing actually needs — [label](url) links and **bold** — and nothing
// else. It matters most on sourced pieces: a guide citing CAES twenty times
// is only credible if those citations are clickable, and crawlers and AI
// readers need the outbound links to see who we are relying on.
//
// Internal paths route through react-router so they don't reload the app.
// External links open in a new tab, with rel set for safety.
const INLINE = /(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)/g;

function RichText({ text, linkClass = "text-teal underline underline-offset-2 hover:text-forest transition-colors" }) {
  if (typeof text !== "string") return text;
  const parts = text.split(INLINE).filter(Boolean);
  return (
    <>
      {parts.map((part, i) => {
        const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
        if (link) {
          const [, label, href] = link;
          if (href.startsWith("/")) {
            return (
              <Link key={i} to={href} className={linkClass}>
                {label}
              </Link>
            );
          }
          return (
            <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
              {label}
            </a>
          );
        }
        const bold = /^\*\*([^*]+)\*\*$/.exec(part);
        if (bold) return <strong key={i} className="font-semibold text-forest-950">{bold[1]}</strong>;
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function Block({ block }) {
  if (block.type === "h2") return <h2 className="font-serif text-2xl sm:text-3xl font-bold text-forest-950 mt-10 mb-3">{block.content}</h2>;
  if (block.type === "p") return <p className="text-[17px] leading-[1.75] text-forest-950/85 mb-5"><RichText text={block.content} /></p>;
  if (block.type === "ul")
    return (
      <ul className="my-5 space-y-2.5">
        {block.content.map((li, i) => (
          <li key={i} className="flex items-start gap-3 text-[17px] leading-relaxed text-forest-950/85">
            <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
            <span><RichText text={li} /></span>
          </li>
        ))}
      </ul>
    );
  if (block.type === "callout")
    return (
      <div className="my-8 flex gap-4 rounded-2xl bg-forest-950 text-cream p-6">
        <DuckBadge size={44} ring={false} />
        <p className="text-[16px] leading-relaxed text-cream/90 italic">
          <RichText text={block.content} linkClass="text-cream underline underline-offset-2 hover:text-sand transition-colors" />
        </p>
      </div>
    );
  // Data tables. Scroll horizontally inside their own container so a wide
  // table never makes the whole page scroll sideways on a phone.
  if (block.type === "table")
    return (
      <figure className="my-8">
        {block.caption && (
          <figcaption className="mb-3 text-sm font-semibold text-forest-950">{block.caption}</figcaption>
        )}
        <div className="overflow-x-auto rounded-2xl border border-forest/12">
          <table className="w-full border-collapse text-sm min-w-[420px]">
            <thead>
              <tr className="bg-forest-950 text-cream">
                {block.head.map((h, i) => (
                  <th key={i} className={`px-4 py-3 font-semibold ${i === 0 ? "text-left" : "text-right"}`}><RichText text={h} linkClass="text-cream underline underline-offset-2" /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className={r % 2 ? "bg-sand/60" : "bg-cream"}>
                  {row.map((cell, i) => (
                    <td key={i} className={`px-4 py-3 border-t border-forest/8 ${i === 0 ? "text-left text-forest-950 font-medium" : "text-right text-forest-950/80 tabular-nums"}`}><RichText text={cell} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {block.source && (
          <p className="mt-2.5 text-xs text-forest-950/55 leading-relaxed">{block.source}</p>
        )}
      </figure>
    );
  // Outbound citations. A data page's authority depends on linking what it cites.
  if (block.type === "sources")
    return (
      <aside className="my-10 rounded-2xl border border-forest/12 bg-sand/60 p-6">
        <h2 className="font-serif text-lg font-bold text-forest-950">{block.caption || "Sources"}</h2>
        <ul className="mt-3 space-y-2.5">
          {block.links.map((l) => (
            <li key={l.url}>
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-teal underline underline-offset-2 hover:text-forest transition-colors leading-relaxed"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      </aside>
    );
  return null;
}

export default function GuideArticle() {
  useSolidHeader();
  const { slug } = useParams();
  const guide = getGuide(slug);
  if (!guide) return <Navigate to="/guides" replace />;

  const related = (guide.related || []).map(getGuide).filter(Boolean).slice(0, 3);
  const ctaPos = ctaIndex(guide.body);
  const fmt = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const dateStr = fmt(guide.date);
  // Only show an "Updated" stamp when it differs from publication — a freshness
  // signal on data pages, and noise everywhere else.
  const updatedStr = guide.updated && guide.updated !== guide.date ? fmt(guide.updated) : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.excerpt,
    image: guide.image,
    datePublished: guide.date,
    dateModified: guide.updated || guide.date,
    author: { "@type": "Organization", name: guide.author },
    publisher: { "@type": "Organization", name: "DuckTheBugs", logo: { "@type": "ImageObject", url: "https://cdn.searchlegends.com/09056a62-59ac-471b-97d8-375e050c6c4d-84jxyx.jpg" } },
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: guide.title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  // Schema answers are plain text — strip the markdown marks so Google and
  // AI readers get the sentence, not "[label](https://…)".
  const plain = (s = "") =>
    String(s).replace(/\[([^\]]+)\]\([^)\s]+\)/g, "$1").replace(/\*\*([^*]+)\*\*/g, "$1");

  const faqLd = guide.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: guide.faq.map((f) => ({
          "@type": "Question",
          name: plain(f.q),
          acceptedAnswer: { "@type": "Answer", text: plain(f.a) },
        })),
      }
    : null;

  return (
    <div className="pt-[72px]" data-testid={`guide-article-${guide.slug}`}>
      <Seo title={guide.seoTitle || guide.title} description={guide.seoDescription || guide.excerpt} path={`/guides/${guide.slug}`} image={guide.image} jsonLd={jsonLd} />

      <article>
        <header className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 pt-12 pb-8">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Guides", to: "/guides" }, { label: guide.category }]} />
          <span className="mt-6 inline-block px-3 py-1 rounded-full bg-terracotta/12 text-terracotta text-xs font-mono uppercase tracking-wider">{guide.category}</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-black text-forest-950 leading-[1.05]">{guide.title}</h1>
          <p className="mt-5 text-xl text-forest-950/70 leading-relaxed">{guide.excerpt}</p>
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4 py-4 border-y border-forest/10">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-forest-950/60">
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{dateStr}</span>
              {updatedStr && (
                <span className="flex items-center gap-1.5 text-forest font-semibold"><RefreshCw className="w-4 h-4" />Updated {updatedStr}</span>
              )}
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{guide.readingTime} min read</span>
              <span>{guide.author}</span>
            </div>
            <button onClick={share} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pill/70 text-forest-950 text-sm font-semibold hover:bg-pill transition-colors" data-testid="guide-share-btn">
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>
        </header>

        <Reveal>
          <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
            <img src={guide.image} alt={guide.imageAlt || guide.title} className="w-full aspect-[16/9] object-cover rounded-[2rem]" />
          </div>
        </Reveal>

        <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
          {guide.body.map((block, i) => (
            <div key={i} className="contents">
              {i === ctaPos && <InlineCTA pest={guide.pest} />}
              <Block block={block} />
            </div>
          ))}

          <div className="mt-10">
            <AffiliateDisclosure compact />
          </div>

          <div className="mt-10 pt-8 border-t border-forest/10 flex items-center justify-between flex-wrap gap-4">
            <Link to="/guides" className="inline-flex items-center gap-2 text-forest font-semibold hover:text-terracotta transition-colors" data-testid="guide-back-link">
              <ArrowLeft className="w-4 h-4" /> All guides
            </Link>
            <Link to="/hire-a-pro" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta/90 transition-all" data-testid="guide-find-pro-btn">
              Find a Pro
            </Link>
          </div>
        </div>
      </article>

      {guide.faq?.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 pb-16">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-forest-950 mb-6">Common questions</h2>
          <dl className="space-y-5">
            {guide.faq.map((f) => (
              <div key={f.q} className="rounded-2xl border border-forest/12 bg-cream p-5">
                <dt className="font-serif text-lg font-bold text-forest-950 leading-snug">{f.q}</dt>
                <dd className="mt-2 text-[16px] text-forest-950/75 leading-relaxed"><RichText text={f.a} /></dd>
              </div>
            ))}
          </dl>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
        </section>
      )}

      {related.length > 0 && (
        <section className="bg-sand grain">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20">
            <Reveal>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-forest-950 mb-8">Keep reading</h2>
            </Reveal>
            <RevealGroup className="grid md:grid-cols-3 gap-5">
              {related.map((g) => (
                <ArticleCard key={g.slug} guide={g} />
              ))}
            </RevealGroup>
          </div>
        </section>
      )}

      <StickyCTA pest={guide.pest} />
    </div>
  );
}
